import _ from 'lodash';
import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';

import toastr from '../../../toastr';
import { Api, IApiResponse } from '../../typings/Api';

interface IZipcodeLookupRecord {
  readonly code: string;
  readonly city: string;
  readonly county: string;
  readonly state: string;
  readonly taxrate: string;
  readonly zipcode: string;
}

interface IProps {
  onEnterKeyPressed: Function;
  onSelected: Function;
}

interface IState {
  value: string;
  suggestions: IZipcodeLookupRecord[];
}

class ZipcodeLookup extends Component<IProps, IState> {
  id = 'zipcodeLookupSK';

  state = {
    suggestions: [],
    value: '',
  };

  get value() {
    return this.state.value;
  }

  set value(value: string) {
    this.setState({
      value,
    });
  }

  focus() {
    const el = document.getElementById(this.id) as HTMLInputElement;
    el.focus();
  }

  getSuggestions = async (value: string) => {
    let res: IApiResponse;

    try {
      res = await Api.get('search/zipcodes', {
        params: {
          limit: 100,
          q: value.trim(),
        },
      });

      if (res.status === 200) {
        this.setState({
          suggestions: res.data,
        });
      } else {
        throw new Error('200 not received from /search/zipcodes');
      }
    } catch (err: any) {
      console.error(err.message);
      toastr.error('Error', 'Zipcode lookup temporarily unavailable.');
      this.setState({
        suggestions: [],
      });
    }
  };

  getSuggestionsDebounced = _.debounce(this.getSuggestions, 250); // tslint:disable-line

  getSuggestionValue = (suggestion: IZipcodeLookupRecord) => `${suggestion.zipcode}.${suggestion.code}`;

  onChange = (syntheticEvent: any, event: Autosuggest.ChangeEvent) => {
    this.setState({
      value: event.newValue,
    });
  };

  onKeyPress = (syntheticEvent: any) => {
    if (syntheticEvent.key === 'Enter') {
      this.props.onEnterKeyPressed();
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  onSuggestionsFetchRequested = (req: Autosuggest.SuggestionsFetchRequestedParams) => {
    // req.reason seems to no longer exist in 9.1.x, docs wrong
    // created issue: https://github.com/moroshko/react-autosuggest/issues/386
    this.getSuggestionsDebounced(req.value);
  };

  onSuggestionSelected = (syntheticEvent: any, selection: Autosuggest.SuggestionSelectedEventData<any>) => {
    this.props.onSelected();
    syntheticEvent.preventDefault();
  };

  renderSuggestion(suggestion: IZipcodeLookupRecord) {
    return (
      <div style={{ fontSize: '14px' }}>
        <div>
          {suggestion.city}, {suggestion.county}, {suggestion.state}
        </div>
        <div>
          Zip: <b>{suggestion.zipcode}</b> Tax: <b>{suggestion.taxrate}</b> {suggestion.code}
        </div>
      </div>
    );
  }

  /* This is used to dynamically calculate the height/width of the suggestion container.

     By default, applying a 100% width to the container would constrain it to the size of its
     containing div.  In situations (like shopkeeper) where that div is part of a flex row of
     other divs, the width would be limited significantly.  This logic allows the width to be
     calculated based on the width of the viewport (sans scroll) and "break out" of said
     containing div.

     Specifically for width, it uses all width between the left edge of the container up to 15px
     shy of the right edge of the viewport (sans scroll).

     Specifically for height, the container will always be AT LEAST 150px (minHeight) tall, and
     AT MOST (maxHeight) a height equal to the distance from the top of the container, to the
     bottom of the viewport, minus 75px (roughly size of actionbar footer in shopkeeper).
  */
  renderSuggestionsContainer = ({ containerProps, children }: any) => {
    const style = {
      maxHeight: '0px',
      minHeight: '0px',
      width: '0px',
    };

    if (
      containerProps.className ===
      'react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open'
    ) {
      const containerRect = (document.getElementById(containerProps.id) as HTMLElement).getBoundingClientRect();
      style.minHeight = '150px';
      style.maxHeight = `${document.body.clientHeight - containerRect.top - 75}px`;
      style.width = `${document.body.clientWidth - containerRect.left - 15}px`;
    }

    return (
      <div {...containerProps} style={style}>
        {children}
      </div>
    );
  };

  render() {
    const { value, suggestions } = this.state;

    const inputProps = {
      id: this.id,
      onChange: this.onChange,
      onKeyPress: this.onKeyPress,
      value,
    };

    return (
      <Autosuggest
        getSuggestionValue={this.getSuggestionValue}
        highlightFirstSuggestion={true}
        id={this.id}
        inputProps={inputProps}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionSelected={this.onSuggestionSelected}
        renderSuggestion={this.renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        suggestions={suggestions}
      />
    );
  }
}

export default ZipcodeLookup;
